/// <amd-module name="Repos/Views/Branches/Components/BranchesView/BranchesView" />
import { EnhancedGitRef } from "Data/EnhancedGitRef";
import * as React from "react";
import {
    GitRef,
    GitRefFavorite,
    GitRefUpdate,
    GitRefUpdateResult,
    GitRepository,
    GitVersionDescriptor,
    GitVersionOptions,
    GitVersionType,
    RefFavoriteType
} from "Repos/Common/Contracts/Git";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { createBranch, deleteBranch } from "Repos/Common/ReposActionService";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchNameToRef, EMPTY_OBJECT_ID, refToBranchName, trimRefs } from "Repos/Common/Util/Ref";
import { gitVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { getReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { ICreateBranchDialogProps, IDeleteBranchDialogProps } from "Repos/VersionDialogs/Dialogs.Props";
import { getGitRefService } from "Repos/VersionDropdown/GitRefService";
import * as Resources from "Repos/Views/Branches/Resources";
import { BranchTypeFilter, DeletedBranch, IBranchesService } from "Services/BranchesService.Types";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import {
    IObservableArray,
    IObservableValue,
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    ObservableCollection,
    ObservableLike,
    ObservableValue
} from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IPageStateHistoryService } from "VSS/Features/History";
import { addMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { getRouteValues } from "VSS/Platform/Navigation";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Tab, TabBar } from "VSSUI/Tabs";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, FILTER_APPLIED_EVENT, IFilterState } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/ZeroData";
import { IBranchTableProvider } from "../../Provider/BranchTableProvider.Types";
import { IBranchTreeProvider } from "../../Provider/BranchTreeProvider.Types";
import { getGitClient, IGitRestClient } from "../../RestClient/Git";
import { getBranchesService } from "../../Services/BranchesService";
import { BranchesTable } from "../BranchesTable/BranchesTable";
import { BranchesTree } from "../BranchesTree/BranchesTree";
import { DeletedBranchesTable } from "../DeletedBranchesTable/DeletedBranchesTable";

const branchTextFilterKey = "text-search";
const ResourceTypeRef = "Ref";

interface SecurityDialogProps {
    title: string;
    permissionSetId: string;
    permissionSetToken: string;
    resourceId: string;
    resourceType: string;
    resourceName: string;
}

class BranchesView extends VssComponent<{}, {}> {
    private readonly projectName: string;
    private readonly repositoryName: string;

    private readonly restClient: IGitRestClient;
    private readonly vcViewModelDataProviderService: IVCViewModelDataProviderService;
    private readonly repository: GitRepository;

    private readonly branchesService: IBranchesService;
    private readonly branchesTreeProviders: { [tabId: string]: IBranchTreeProvider } = {};
    private readonly currentBranchesTreeProvider = new ObservableValue<IBranchTreeProvider | undefined>(undefined);
    private readonly staleBranchesTableProvider = new ObservableValue<IBranchTableProvider | undefined>(undefined);
    private readonly deletedBranchesTableProvider = new ObservableValue<IObservableArray<DeletedBranch | undefined> | undefined>(undefined);

    private readonly selectedTabId: IReadonlyObservableValue<string>;

    private readonly branchesFilter = new Filter();
    private isCurrentProviderFilterProvider = false;

    private readonly routingService: IReposRoutingService;

    private readonly doesCurrentProviderHaveLoadedData = new ObservableValue<boolean>(false);
    private readonly zeroDataUrl: string;

    private readonly showBranchSecurityDialog = new ObservableValue<boolean>(false);
    private readonly securityDialogProps = new ObservableValue<SecurityDialogProps>({
        title: "",
        permissionSetId: "",
        permissionSetToken: "",
        resourceId: "",
        resourceType: "",
        resourceName: ""
    });

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        // Use the routeValues to determine the current project and repoName
        const routeValues = getRouteValues(context.pageContext);
        this.projectName = routeValues["project"];
        this.repositoryName = routeValues["GitRepositoryName"] || this.projectName;

        this.routingService = context.pageContext.getService<IReposRoutingService>("IReposRoutingService");

        const pageStateHistoryService = context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const queryParams = pageStateHistoryService.getProjectedState();
        if (queryParams["baseVersion"] && queryParams["targetVersion"]) {
            const fpsUrl = this.routingService.getBranchCompareUrl(
                this.repositoryName,
                this.projectName,
                gitVersionStringToVersionDescriptor(queryParams["baseVersion"]),
                gitVersionStringToVersionDescriptor(queryParams["targetVersion"])
            );
            FastPageSwitch(context.pageContext, fpsUrl, false);
        }

        this.vcViewModelDataProviderService = context.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        this.repository = this.vcViewModelDataProviderService.getRepository()!;

        this.restClient = getGitClient(context.pageContext);

        this.zeroDataUrl = getAssetLocation(context.pageContext, "ms.vss-code-web/branches-content/Content/no-results.png");

        // Initialize branchesTreeProvider
        this.branchesService = getBranchesService(this.context.pageContext);
        this.selectedTabId = this.branchesService.getSelectedTabId();

        const markBranchTTI = () => performanceService.markTimeToInteractive("Repos", "branches-hub");

        const performanceService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        if (this.selectedTabId.value === BranchTypeFilter.Stale) {
            this.staleBranchesTableProvider = new ObservableValue(
                this.getBranchTableProvider(this.selectedTabId.value as BranchTypeFilter, markBranchTTI)
            );
        } else {
            this.currentBranchesTreeProvider = new ObservableValue(
                this.getBranchTreeProvider(this.selectedTabId.value as BranchTypeFilter, undefined, markBranchTTI)
            );
        }
    }

    public render() {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow custom-scrollbar scroll-auto-hide">
                    <Header commandBarItems={this.getHeaderCommandBarItems()} title={Resources.BranchesHeaderTitle} titleSize={TitleSize.Large} />

                    <TabBar
                        className="scroll-hidden flex-wrap"
                        onSelectedTabChanged={this.onTabChanged}
                        selectedTabId={this.selectedTabId}
                        renderAdditionalContent={this.renderInlineFilterBarItem}
                    >
                        <Tab name={Resources.MyBranches} id={BranchTypeFilter.Mine} />
                        <Tab name={Resources.AllBranches} id={BranchTypeFilter.All} />
                        <Tab name={Resources.StaleBranches} id={BranchTypeFilter.Stale} />
                    </TabBar>

                    <div className="page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                        <ReposMessages className="flex-noshrink" />
                        <Observer selectedTabId={this.selectedTabId}>
                            {(observerProps: { selectedTabId: string }) => {
                                switch (observerProps.selectedTabId) {
                                    case BranchTypeFilter.Stale:
                                        return (
                                            <Observer
                                                branchProvider={this.staleBranchesTableProvider}
                                                doesCurrentProviderHaveLoadedData={this.doesCurrentProviderHaveLoadedData}
                                            >
                                                {(observerProps: {
                                                    branchProvider: IBranchTableProvider | undefined;
                                                    doesCurrentProviderHaveLoadedData: boolean;
                                                }) => {
                                                    if (observerProps.branchProvider) {
                                                        if (
                                                            observerProps.doesCurrentProviderHaveLoadedData &&
                                                            observerProps.branchProvider.baseProvider.length === 0
                                                        ) {
                                                            return (
                                                                <BranchesZeroData imageUrl={this.zeroDataUrl} text={Resources.NoStaleResultsText} />
                                                            );
                                                        } else {
                                                            return (
                                                                <BranchesTable
                                                                    key={this.selectedTabId.value}
                                                                    addContributedMenuItems={this.addContributedMenuItems}
                                                                    isUserMember={userHasClaim(this.context.pageContext, UserClaims.Member)}
                                                                    itemProvider={observerProps.branchProvider}
                                                                    onCheckPermission={this.checkPermission}
                                                                    onCreate={this.showAddBranchDialog}
                                                                    onDelete={this.showDeleteBranchConfirmationDialog}
                                                                    onLock={ref => this.lockUnlockBranch(ref, true)}
                                                                    onRowActivate={branch =>
                                                                        FastPageSwitch(this.context.pageContext, branch.refUrl, true)
                                                                    }
                                                                    onSetAsCompare={this.setAsCompare}
                                                                    onSetAsDefault={this.setAsDefault}
                                                                    onSetFavorite={this.setFavoriteBranch}
                                                                    onShowSecurityDialog={this.showSecurityDialog}
                                                                    onUnlock={ref => this.lockUnlockBranch(ref, false)}
                                                                />
                                                            );
                                                        }
                                                    }

                                                    return null;
                                                }}
                                            </Observer>
                                        );
                                    case BranchTypeFilter.Mine:
                                    case BranchTypeFilter.All:
                                        return (
                                            <Observer
                                                branchProvider={this.currentBranchesTreeProvider}
                                                deletedProvider={this.deletedBranchesTableProvider}
                                                doesCurrentProviderHaveLoadedData={this.doesCurrentProviderHaveLoadedData}
                                            >
                                                {(observerProps: {
                                                    branchProvider: IBranchTreeProvider | undefined;
                                                    deletedProvider: IReadonlyObservableArray<DeletedBranch | undefined> | undefined;
                                                    doesCurrentProviderHaveLoadedData: boolean;
                                                }) => {
                                                    if (observerProps.branchProvider) {
                                                        const filterState = this.branchesFilter.getFilterItemState(branchTextFilterKey);
                                                        const filterValue = filterState && filterState.value;

                                                        let deletedTable: JSX.Element | null = null;
                                                        if (observerProps.deletedProvider) {
                                                            deletedTable = (
                                                                <DeletedBranchesTable
                                                                    onRestore={this.onRestoreBranch}
                                                                    provider={observerProps.deletedProvider}
                                                                    repository={this.repository}
                                                                />
                                                            );
                                                        }

                                                        return (
                                                            <UncheckedObserver branchProviderBase={observerProps.branchProvider.baseProvider}>
                                                                {(innerObserverProps: { branchProviderBase: EnhancedGitRef[] }) => {
                                                                    if (
                                                                        observerProps.doesCurrentProviderHaveLoadedData &&
                                                                        innerObserverProps.branchProviderBase.length === 0
                                                                    ) {
                                                                        return (
                                                                            <>
                                                                                <BranchesZeroData
                                                                                    imageUrl={this.zeroDataUrl}
                                                                                    text={Resources.NoResultsText}
                                                                                />
                                                                                {deletedTable}
                                                                            </>
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <>
                                                                                <BranchesTree
                                                                                    key={
                                                                                        filterValue
                                                                                            ? `filter-${filterValue}`
                                                                                            : this.selectedTabId.value
                                                                                    }
                                                                                    branchProvider={observerProps.branchProvider!}
                                                                                    addContributedMenuItems={this.addContributedMenuItems}
                                                                                    isUserMember={userHasClaim(
                                                                                        this.context.pageContext,
                                                                                        UserClaims.Member
                                                                                    )}
                                                                                    onBranchRowActivate={branch =>
                                                                                        FastPageSwitch(this.context.pageContext, branch.refUrl, true)
                                                                                    }
                                                                                    onCheckPermission={this.checkPermission}
                                                                                    onCreate={this.showAddBranchDialog}
                                                                                    onDelete={this.showDeleteBranchConfirmationDialog}
                                                                                    onLock={ref => this.lockUnlockBranch(ref, true)}
                                                                                    onSetAsCompare={this.setAsCompare}
                                                                                    onSetAsDefault={this.setAsDefault}
                                                                                    onSetFavoriteBranch={this.setFavoriteBranch}
                                                                                    onSetFavoriteFolder={this.setFavoriteFolder}
                                                                                    onShowSecurityDialog={this.showSecurityDialog}
                                                                                    onUnlock={ref => this.lockUnlockBranch(ref, false)}
                                                                                    projectName={this.projectName}
                                                                                    repositoryName={this.repositoryName}
                                                                                    routingService={this.routingService}
                                                                                />
                                                                                {deletedTable}
                                                                            </>
                                                                        );
                                                                    }
                                                                }}
                                                            </UncheckedObserver>
                                                        );
                                                    }

                                                    return null;
                                                }}
                                            </Observer>
                                        );
                                }
                            }}
                        </Observer>

                        <ConditionalChildren renderChildren={this.showBranchSecurityDialog}>
                            <Observer dialogProps={this.securityDialogProps}>
                                {(observerProps: { dialogProps: SecurityDialogProps }) => (
                                    <WrappedComponent
                                        dependencies={["ms.vss-admin-web.security-view-common-content"]}
                                        wrappedType="ms.vss-admin-web.security-panel-component"
                                        onCancel={() => (this.showBranchSecurityDialog.value = false)}
                                        securityViewTitle={observerProps.dialogProps.title}
                                        permissionSetId={observerProps.dialogProps.permissionSetId}
                                        permissionSetToken={observerProps.dialogProps.permissionSetToken}
                                        resourceId={observerProps.dialogProps.resourceId}
                                        resourceType={observerProps.dialogProps.resourceType}
                                        resourceName={observerProps.dialogProps.resourceName}
                                    />
                                )}
                            </Observer>
                        </ConditionalChildren>
                    </div>
                </Page>
            </Surface>
        );
    }

    public componentDidMount() {
        this.branchesFilter.subscribe(this.onFilterChange, FILTER_APPLIED_EVENT);
    }

    private addContributedMenuItems = (branchName: string, item: EnhancedGitRef | undefined, collection: ObservableCollection<IMenuItem>) => {
        const contributionContext = item
            ? {
                  name: branchName,
                  fullName: item.rootRef.name,
                  isFolder: false,
                  comment: item.metadata?.comment,
                  commitId: item.metadata?.commitId,
                  isUserCreated: item.isUserCreated,
                  isDefault: item.isDefault,
                  isCompare: item.isCompare,
                  lastUpdatedBy: item.metadata?.committer.name,
                  aheadCount: item.stats.value?.aheadCount,
                  behindCount: item.stats.value?.behindCount,
                  pullRequestId: item?.pullRequest?.pullRequestId,
                  pullRequestUrl: item?.pullRequestUrl,
                  favorite: Boolean(item?.favorite),
                  push: item?.metadata?.push
              }
            : {
                  name: branchName,
                  isFolder: true
              };

        addMenuItems(this.context.pageContext, collection, "ms.vss-code-web.git-branches-tree-menu", contributionContext);
    };

    private renderInlineFilterBarItem = () => {
        return (
            <InlineKeywordFilterBarItem filter={this.branchesFilter} filterItemKey={branchTextFilterKey} placeholder={Resources.SearchBranchName} />
        );
    };

    private calculateGitSecuredToken = (projectGuid: string, repoId: string, branchName: string) => {
        const partialProjectToken = projectGuid ? projectGuid + "/" : "";
        const partialRepoToken = partialProjectToken && repoId ? repoId + "/" : "";
        const partialBranchToken = partialRepoToken && branchName ? branchName.split("/").join("^") + "/" : "";

        return "repoV2/" + partialProjectToken + partialRepoToken + partialBranchToken;
    };

    private calculateResourceId = (projectGuid: string, repoId: string, branchName: string) => {
        const partialProjectToken = projectGuid ? projectGuid + "/" : "";
        const partialRepoToken = partialProjectToken && repoId ? repoId + "/" : "";
        const partialBranchToken = partialRepoToken && branchName ? branchName : "";
        return partialProjectToken + partialRepoToken + partialBranchToken;
    };

    private checkPermission = (permission: GitRepositoryPermissions, branch?: GitRef) => {
        const permissionService = this.context.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");
        return permissionService.hasPermission(
            permission,
            branch && {
                version: refToBranchName(branch.name),
                versionOptions: GitVersionOptions.None,
                versionType: GitVersionType.Branch
            },
            this.repository && this.repository.id,
            this.repository && this.repository.project.id
        );
    };

    private getBranchTableProvider = (tabName: BranchTypeFilter, onDataLoaded?: () => void) => {
        if (this.staleBranchesTableProvider.value) {
            this.doesCurrentProviderHaveLoadedData.value = true;
            return this.staleBranchesTableProvider.value;
        }

        this.doesCurrentProviderHaveLoadedData.value = false;

        return this.branchesService.getBranchTableProvider(
            {
                projectName: this.projectName,
                repositoryName: this.repositoryName,
                branchTypeFilter: tabName
            },
            () => {
                this.onProviderDataLoaded(tabName);
                onDataLoaded && onDataLoaded();
            }
        );
    };

    private getBranchTreeProvider = (tabName: BranchTypeFilter, filterText?: string, onDataLoaded?: () => void) => {
        // Don't look at the cache if we're using filter text
        if (!filterText && this.branchesTreeProviders[tabName]) {
            this.doesCurrentProviderHaveLoadedData.value = true;
            return this.branchesTreeProviders[tabName];
        }

        $DEBUG &&
            filterText &&
            tabName !== BranchTypeFilter.All &&
            console.warn(
                "Attempted to get a filtered tree provider without using the All provider; there's an error in code. Using the All provider anyway"
            );

        this.doesCurrentProviderHaveLoadedData.value = false;
        const treeProvider = this.branchesService.getBranchTreeProvider(
            {
                projectName: this.projectName,
                repositoryName: this.repositoryName,
                branchTypeFilter: filterText ? BranchTypeFilter.All : tabName,
                filterText
            },
            () => {
                this.onProviderDataLoaded(tabName, filterText);
                onDataLoaded && onDataLoaded();
            }
        );

        // Cache the provider if we didn't get any filter text
        if (!filterText) {
            this.branchesTreeProviders[tabName] = treeProvider;
        }
        return treeProvider;
    };

    private getHeaderCommandBarItems = () => {
        const items: IHeaderCommandBarItem[] = [];

        if (this.checkPermission(GitRepositoryPermissions.CreateBranch)) {
            items.push({
                id: "add",
                className: "create-new-branch",
                text: Resources.CreateNewBranch,
                onActivate: () => this.showAddBranchDialog(),
                isPrimary: true
            });
        }

        return items;
    };

    private lockUnlockBranch = (branch: EnhancedGitRef, isLocked: boolean) => {
        if (this.repository) {
            const refUpdate = {
                isLocked: isLocked
            } as GitRefUpdate;

            // For whatever reason we have to strip refs/ off of the branch or the server will reject it as "refs/refs/..."
            let branchName = branch.rootRef.name;
            if (branchName.indexOf("refs/") === 0) {
                branchName = branchName.substring(5);
            }

            this.restClient.updateRef(refUpdate, this.repository.id, branchName).then(result => {
                const newBranch: EnhancedGitRef = { ...branch, rootRef: result };
                this.staleBranchesTableProvider.value && this.staleBranchesTableProvider.value.update(newBranch);
                this.visitAllTreeProviders(provider => provider.baseProvider.update(newBranch));
            }, this.showError);
        }
    };

    private onFilterChange = (filterState: IFilterState) => {
        const filterItemState = filterState[branchTextFilterKey];
        if (filterItemState && filterItemState.value === "") {
            getBranchesService(this.context.pageContext).setBranchesFilter("");
        }
        if (filterItemState && filterItemState.value !== "") {
            this.branchesService.setSelectedTabId(BranchTypeFilter.All);

            this.currentBranchesTreeProvider.value = this.getBranchTreeProvider(BranchTypeFilter.All, filterItemState.value);
            this.deletedBranchesTableProvider.value = getBranchesService(this.context.pageContext).searchDeletedBranches(
                this.repository.id,
                this.repository.project.id,
                filterItemState.value
            );
            this.isCurrentProviderFilterProvider = true;
        } else if (this.isCurrentProviderFilterProvider) {
            // Only reset if we had a filterProvider before; otherwise we don't need to update anything
            // This allows us to dodge this block on Clears and saves us several rest calls and several observable updates / renders on tab switch
            this.currentBranchesTreeProvider.value = this.getBranchTreeProvider(this.selectedTabId.value as BranchTypeFilter);
            this.deletedBranchesTableProvider.value = undefined;
            this.isCurrentProviderFilterProvider = false;
        }
    };

    private onProviderDataLoaded = (providerTabName: BranchTypeFilter, providerFilterText?: string) => {
        // Verify this provider is the currently active provider
        if (providerTabName === this.selectedTabId.value) {
            const filterState = this.branchesFilter.getFilterItemState(branchTextFilterKey);
            const currentFilterValue = filterState && filterState.value;

            if (!providerFilterText || (providerFilterText === currentFilterValue && this.isCurrentProviderFilterProvider)) {
                this.doesCurrentProviderHaveLoadedData.value = true;
            }
        }
    };

    private onRestoreBranch = (deletedBranch: DeletedBranch) => {
        const newBranch = {
            name: deletedBranch.refName,
            newObjectId: deletedBranch.oldObjectId,
            oldObjectId: EMPTY_OBJECT_ID
        } as GitRefUpdate;

        const globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        this.restClient.updateRefs([newBranch], this.repository.id, this.repository.project.id).then(updateResult => {
            if (updateResult[0].success) {
                getGitRefService(this.context.pageContext).flushBranchesCache(this.repository.id);

                Promise.all([
                    this.restClient.getRefs(this.repository.id, this.projectName, trimRefs(deletedBranch.refName)),
                    this.restClient.getCommit(deletedBranch.oldObjectId, this.repository.id, this.projectName)
                ]).then(promiseResults => {
                    globalMessageService.addToast({
                        duration: 3000,
                        message: format(Resources.SuccessfullyRestoredBranch, deletedBranch.branchName)
                    });
                    const [fetchedRefs, metadata] = promiseResults;
                    const enhancedRef =
                        this.currentBranchesTreeProvider.value && this.currentBranchesTreeProvider.value.createEnhancedRef(fetchedRefs[0], metadata);

                    // Assume restored branches won't be stale, so don't add them to the stale provider
                    this.visitAllTreeProviders(provider => provider.baseProvider.add(enhancedRef));
                    this.deletedBranchesTableProvider.value &&
                        this.deletedBranchesTableProvider.value.removeAll(x => {
                            const value = ObservableLike.getValue(x);
                            return value ? value.pushId === deletedBranch.pushId : false;
                        });
                });
            } else {
                globalMessageService.addToast({ duration: 3000, message: format(Resources.FailedRestoredBranch, deletedBranch.branchName) });
            }
        });
    };

    private onTabChanged = (newTabName: string) => {
        this.branchesService.setSelectedTabId(newTabName);

        switch (newTabName) {
            case BranchTypeFilter.All:
            case BranchTypeFilter.Mine:
                this.currentBranchesTreeProvider.value = this.getBranchTreeProvider(newTabName as BranchTypeFilter);
                break;
            case BranchTypeFilter.Stale:
                this.staleBranchesTableProvider.value = this.getBranchTableProvider(newTabName as BranchTypeFilter);
                break;
        }
        this.deletedBranchesTableProvider.value = undefined;
        this.isCurrentProviderFilterProvider = false;
        this.branchesFilter.reset();
    };

    private setAsCompare = (branch: EnhancedGitRef) => {
        if (this.repository) {
            const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

            const entries: { [key: string]: string } = {};
            entries["Branches.Compare"] = branch.rootRef.name;
            settingsService.setEntries(entries, SettingsUserScope.Me, "Repository", this.repository.id).then(() => {
                this.staleBranchesTableProvider.value && this.staleBranchesTableProvider.value.setCompareBranch(branch, true);
                this.visitAllTreeProviders(branchProvider => branchProvider.setCompareBranch(branch, true));
            }, this.showError);
        }
    };

    private setAsDefault = (branch: EnhancedGitRef) => {
        if (this.repository) {
            this.branchesService.setDefaultBranch(this.repository.project.id, this.repository.id, branch.rootRef.name).then(result => {
                if (result) {
                    this.staleBranchesTableProvider.value && this.staleBranchesTableProvider.value.setDefaultBranch(branch);
                    this.visitAllTreeProviders(branchProvider => branchProvider.setDefaultBranch(branch));
                }
            }, this.showError);
        }
    };

    private setFavoriteBranch = (branch: EnhancedGitRef, state: boolean) => {
        if (this.repository) {
            if (state) {
                // Create favorite
                const properties: GitRefFavorite = {
                    name: branchNameToRef(branch.rootRef.name),
                    repositoryId: this.repository.id,
                    type: RefFavoriteType.Ref
                } as GitRefFavorite;

                this.restClient.createFavorite(properties, this.projectName).then(result => {
                    const newBranch = { ...branch, favorite: result };
                    this.staleBranchesTableProvider.value && this.staleBranchesTableProvider.value.update(newBranch);
                    this.visitAllTreeProviders(provider => provider.baseProvider.update(newBranch));
                }, this.showError);
            } else if (branch.favorite) {
                // Delete favorite
                this.restClient.deleteRefFavorite(this.projectName, branch.favorite.id).then(() => {
                    const newBranch = { ...branch, favorite: undefined };
                    this.staleBranchesTableProvider.value && this.staleBranchesTableProvider.value.update(newBranch);
                    this.visitAllTreeProviders(provider => provider.baseProvider.update(newBranch));
                }, this.showError);
            }
        }
    };

    private setFavoriteFolder = (path: string, state: boolean) => {
        path = branchNameToRef(path);

        if (this.repository) {
            const currentFavoriteData =
                this.currentBranchesTreeProvider.value && this.currentBranchesTreeProvider.value.getFolderFavorite(path).value;

            if (state) {
                // Create favorite
                const properties: GitRefFavorite = {
                    name: path,
                    repositoryId: this.repository.id,
                    type: RefFavoriteType.Folder
                } as GitRefFavorite;

                this.restClient.createFavorite(properties, this.projectName).then(result => {
                    this.visitAllTreeProviders(provider => provider.setFolderFavorite(path, result));
                }, this.showError);
            } else if (currentFavoriteData) {
                // Delete favorite
                this.restClient.deleteRefFavorite(this.projectName, currentFavoriteData.id).then(() => {
                    // Folders don't display on stale; don't update it
                    this.visitAllTreeProviders(provider => provider.setFolderFavorite(path, undefined));
                }, this.showError);
            }
        }
    };

    private showAddBranchDialog = (baseBranch?: EnhancedGitRef, onDismiss?: () => void) => {
        let referenceVersion: GitVersionDescriptor | undefined;
        if (baseBranch) {
            const referenceBranchName = refToBranchName(baseBranch.rootRef.name);
            referenceVersion = {
                version: referenceBranchName,
                versionOptions: GitVersionOptions.None,
                versionType: GitVersionType.Branch
            };
        } else {
            const targetProvider = this.currentBranchesTreeProvider.value;
            const defaultBaseBranch = targetProvider?.getDefaultBranch() || targetProvider?.getCompareBranch();
            if (defaultBaseBranch) {
                referenceVersion = {
                    version: refToBranchName(defaultBaseBranch.rootRef.name),
                    versionOptions: GitVersionOptions.None,
                    versionType: GitVersionType.Branch
                };
            }
        }

        createBranch(this.context.pageContext, {
            onComplete: (createdBranch: GitRefUpdate) => {
                getGitRefService(this.context.pageContext).flushBranchesCache(this.repository.id);

                Promise.all([
                    this.restClient.getRefs(createdBranch.repositoryId, this.projectName, trimRefs(createdBranch.name)),
                    this.restClient.getCommit(createdBranch.newObjectId, createdBranch.repositoryId, this.projectName)
                ]).then(promiseResults => {
                    const [fetchedRefs, metadata] = promiseResults;
                    const enhancedRef =
                        this.currentBranchesTreeProvider.value && this.currentBranchesTreeProvider.value.createEnhancedRef(fetchedRefs[0], metadata);

                    // New branches won't be stale, so don't add them to the stale provider ever
                    this.visitAllTreeProviders(provider => provider.baseProvider.add(enhancedRef));
                });
            },
            onDismiss,
            referenceVersion,
            repository: this.repository
        } as ICreateBranchDialogProps);
    };

    private showDeleteBranchConfirmationDialog = (branch: EnhancedGitRef, onDismiss?: () => void) => {
        const branchName = refToBranchName(branch.rootRef.name);
        const branchToDelete: GitVersionDescriptor = {
            version: branchName,
            versionOptions: GitVersionOptions.None,
            versionType: GitVersionType.Branch
        };

        deleteBranch(this.context.pageContext, {
            branchToDelete,
            onComplete: (deletedBranch: GitRefUpdateResult) => {
                const targetProvider = this.currentBranchesTreeProvider.value;
                const defaultBranch = targetProvider?.getDefaultBranch();
                if (defaultBranch && branch.isCompare) {
                    this.setAsCompare(defaultBranch);
                }
                getGitRefService(this.context.pageContext).flushBranchesCache(this.repository.id);
                this.staleBranchesTableProvider.value && this.staleBranchesTableProvider.value.remove(branch);
                this.visitAllTreeProviders(provider => provider.baseProvider.remove(branch));
            },
            onDismiss,
            repository: this.repository
        } as IDeleteBranchDialogProps);
    };

    private showError = (error: Error): void => {
        getReposMessagesService(this.context.pageContext).showMessage({
            renderMessage: () => error.message,
            severity: MessageCardSeverity.Error
        });
    };

    private showSecurityDialog = (branchName: string) => {
        this.securityDialogProps.value = {
            title: format(Resources.BranchSecurityDialogTitle, branchName, this.projectName),
            permissionSetId: this.vcViewModelDataProviderService.getViewModel().repositoryPermissionSet,
            permissionSetToken: this.calculateGitSecuredToken(
                this.repository ? this.repository.project.id : "",
                this.repository ? this.repository.id : "",
                branchNameToRef(branchName)
            ),
            resourceId: this.calculateResourceId(
                this.repository ? this.repository.project.id : "",
                this.repository ? this.repository.id : "",
                branchNameToRef(branchName)
            ),
            resourceType: ResourceTypeRef,
            resourceName: branchName
        };

        this.showBranchSecurityDialog.value = true;
    };

    private visitAllTreeProviders = (visitor: (branchProvider: IBranchTreeProvider) => void) => {
        if (this.isCurrentProviderFilterProvider && this.currentBranchesTreeProvider.value) {
            visitor(this.currentBranchesTreeProvider.value);
        }

        Object.keys(this.branchesTreeProviders).forEach(key => {
            visitor(this.branchesTreeProviders[key]);
        });
    };
}

Components.add("branchesView", BranchesView);

interface IBranchesZeroDataProps {
    imageUrl: string;
    text: string;
}

const BranchesZeroData: React.FunctionComponent<IBranchesZeroDataProps> = props => {
    const { imageUrl, text } = props;

    return (
        <Card className="repos-branches-zero-data-card flex-center" contentProps={{ contentPadding: false }}>
            <ZeroData className="margin-top-16" imagePath={imageUrl} imageAltText={text} secondaryText={<div className="title-s">{text}</div>} />
        </Card>
    );
};
